import React from 'react';

const fmtMoney = (n) => {
  const v = Number(n);
  return (Number.isFinite(v) ? v : 0).toLocaleString('es-PE', {
    style: 'currency',
    currency: 'PEN',
    minimumFractionDigits: 2,
  });
};

const fmtQty = (n) => {
  const v = Number(n);
  return (Number.isFinite(v) ? v : 0).toLocaleString('es-PE', { maximumFractionDigits: 4 });
};

function MetaRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="pg-meta-row">
      <dt>{label}:</dt>
      <dd>{value}</dd>
    </div>
  );
}

function InsumosTable({ rows, showOrigen = false }) {
  if (!rows?.length) return <p className="pg-muted">Sin registros.</p>;
  return (
    <table>
      <thead>
        <tr>
          {showOrigen ? <th>Origen</th> : null}
          <th>Producto / insumo</th>
          <th className="num">Cant. necesaria</th>
          <th className="num">Despacho</th>
          <th>Unidad</th>
          <th className="num">C. unit.</th>
          <th className="num">Subtotal</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={`${row.product}-${row.unit}-${i}`}>
            {showOrigen ? <td>{row.origen}</td> : null}
            <td>{row.product}</td>
            <td className="num">{fmtQty(row.quantity)}</td>
            <td className="num">{fmtQty(row.cantidadDespacho)}</td>
            <td>{row.unit}</td>
            <td className="num">{fmtQty(row.costoUnitario)}</td>
            <td className="num">{fmtMoney(row.subtotal)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const ProduccionGuiaPrintDocument = ({ informe }) => {
  if (!informe) return null;
  const { guia, meta, guiasVinculadas, elaborados, insumosPep, insumosPt, subproductosPep, coproductosPep, subproductosRecuperados, totales } =
    informe;

  const insumosCombinados = [...(insumosPep || []), ...(insumosPt || [])];

  return (
    <div className="pg-print-doc">
      <h1>{meta.tipoTitulo}</h1>
      <p className="pg-subtitle">Informe de costos, insumos y guías de inventario vinculadas</p>
      <div className="pg-line" />

      <div className="pg-meta">
        <div>
          <MetaRow label="Código" value={guia.code} />
          <MetaRow label="Fecha / hora" value={`${guia.guide_date || ''} ${guia.guide_time || ''}`.trim()} />
          <MetaRow label="Almacén" value={guia.warehouse_name} />
          <MetaRow label="Proyecto / operación" value={guia.project_name} />
        </div>
        <div>
          <MetaRow label="Línea" value={guia.produccion_linea_name} />
          <MetaRow label="Recetas con cantidad" value={String(guia.recetas_count)} />
          <MetaRow label="Corte kárdex" value={meta.kardexCorte} />
          <div className="pg-meta-row">
            <dt>Estado:</dt>
            <dd>
              <span className="pg-status">{guia.production_status_label}</span>
            </dd>
          </div>
        </div>
      </div>

      {guiasVinculadas?.length > 0 ? (
        <>
          <h2>Guías de inventario vinculadas</h2>
          <ul className="pg-guides-list">
            {guiasVinculadas.map((g, i) => (
              <li key={`${g.tipo}-${g.code}-${i}`}>
                <strong>{g.tipo}:</strong> <span className="font-mono">{g.code}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h2>Lo que se está produciendo ({elaborados?.length || 0} ítem(s))</h2>
      {elaborados?.length ? (
        <table>
          <thead>
            <tr>
              <th>Receta</th>
              <th>Clasificación</th>
              <th className="num">Cant. elaborada</th>
              <th className="num">C. unitario</th>
              <th className="num">C. total</th>
            </tr>
          </thead>
          <tbody>
            {elaborados.map((row) => (
              <tr key={`${row.receta_name}-${row.receta_code}`}>
                <td>
                  <strong>{row.receta_name}</strong>
                  {row.receta_code ? <span className="pg-muted"> ({row.receta_code})</span> : null}
                </td>
                <td>{row.classification}</td>
                <td className="num">{fmtQty(row.quantity)} {row.unit}</td>
                <td className="num">{fmtMoney(row.unitPrice)}</td>
                <td className="num">{fmtMoney(row.totalCost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="pg-muted">Sin elaborados registrados.</p>
      )}

      <div className="pg-total-box">
        <strong>Costo total elaborados:</strong> {fmtMoney(totales.costoElaboradosTotal)}
      </div>

      <h2>Insumos y costos (kárdex al corte)</h2>
      {totales.tienePep ? (
        <>
          <h3 style={{ fontSize: '11px', fontWeight: 600, margin: '8px 0 4px' }}>Fabricación PEP (subproductos)</h3>
          <InsumosTable rows={insumosPep} />
          <h3 style={{ fontSize: '11px', fontWeight: 600, margin: '12px 0 4px' }}>Producto terminado</h3>
          <InsumosTable rows={insumosPt} />
        </>
      ) : (
        <InsumosTable rows={insumosCombinados} />
      )}

      <div className="pg-total-box">
        <strong>Costo total insumos:</strong> {fmtMoney(totales.costoInsumosTotal)}
      </div>

      {subproductosPep?.length > 0 ? (
        <>
          <h2>Subproductos PEP generados</h2>
          <table>
            <thead>
              <tr>
                <th>Producto</th>
                <th className="num">Cantidad</th>
                <th className="num">C. unit.</th>
                <th className="num">C. total</th>
              </tr>
            </thead>
            <tbody>
              {subproductosPep.map((r, i) => (
                <tr key={`pep-out-${i}`}>
                  <td>{r.product}</td>
                  <td className="num">{fmtQty(r.quantity)} {r.unit}</td>
                  <td className="num">{fmtMoney(r.unitPrice)}</td>
                  <td className="num">{fmtMoney(r.totalCost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      {coproductosPep?.length > 0 ? (
        <>
          <h2>Co-productos de fabricación PEP</h2>
          <table>
            <thead>
              <tr>
                <th>Producto</th>
                <th>Origen PEP</th>
                <th className="num">Cantidad</th>
                <th className="num">C. total</th>
              </tr>
            </thead>
            <tbody>
              {coproductosPep.map((r, i) => (
                <tr key={`co-${i}`}>
                  <td>{r.product}</td>
                  <td>{r.source || '—'}</td>
                  <td className="num">{fmtQty(r.quantity)} {r.unit}</td>
                  <td className="num">{fmtMoney(r.totalCost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      {subproductosRecuperados?.length > 0 ? (
        <>
          <h2>Subproductos recuperados de insumos</h2>
          <table>
            <thead>
              <tr>
                <th>Producto</th>
                <th className="num">Cantidad</th>
                <th>Unidad</th>
              </tr>
            </thead>
            <tbody>
              {subproductosRecuperados.map((r, i) => (
                <tr key={`bp-${i}`}>
                  <td>{r.product}</td>
                  <td className="num">{fmtQty(r.quantity)}</td>
                  <td>{r.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      {guia.notes ? (
        <div className="pg-notes">
          <strong>Observaciones de la guía:</strong>
          <div>{guia.notes}</div>
        </div>
      ) : null}

      <p className="pg-muted" style={{ marginTop: 16 }}>
        Generado el {new Date(meta.generatedAt).toLocaleString('es-PE')} · Contify Logistic
      </p>
    </div>
  );
};

export default ProduccionGuiaPrintDocument;
